import { useState } from "react";
import { Mail, MapPin, Phone, Send, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const SUBJECTS = ["General", "Bookings", "Shopping", "Complaints", "Others"];

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/inquiries", payload);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "General", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send. Try again.");
    } finally { setLoading(false); }
  };

  const wa = `https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}`;

  return (
    <div data-testid="contact-page" className="bg-[var(--pz-ink)] text-white">
      <SEOHead title="Contact — The Arena" description="Get in touch — book courts, register teams or enquire about corporate tournaments." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-4">Talk to us</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold uppercase leading-none">Book the arena.</h1>
            <p className="mt-6 text-zinc-400 text-lg max-w-xl">Drop us a message below — or hit us on WhatsApp for the fastest answer. We usually reply within an hour.</p>

            <ul className="mt-10 space-y-4 text-zinc-300">
              <li className="flex gap-3 items-start"><MapPin className="h-5 w-5 text-[var(--pz-red)] mt-0.5" /> {settings.address}</li>
              <li className="flex gap-3 items-start"><Phone className="h-5 w-5 text-[var(--pz-red)] mt-0.5" /> {settings.contact_phone}</li>
              <li className="flex gap-3 items-start"><Mail className="h-5 w-5 text-[var(--pz-red)] mt-0.5" /> {settings.contact_email}</li>
            </ul>

            <a href={wa} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-cta" className="mt-10 inline-flex"><span className="btn-red"><MessageCircle className="h-4 w-4" /> Chat on WhatsApp</span></a>

            {settings.map_embed && <div className="mt-10 rounded-md overflow-hidden" dangerouslySetInnerHTML={{ __html: settings.map_embed }} />}
          </div>

          <form onSubmit={onSubmit} data-testid="contact-form" className="bg-[var(--pz-ink-2)] border border-[var(--pz-line)] rounded-md p-8 space-y-4 self-start">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
              <Field label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="contact-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email (optional)" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">Subject <span className="text-[var(--pz-red)]">*</span></label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject"
                  required
                  className="w-full px-5 py-3 rounded-md bg-[var(--pz-ink)] border border-[var(--pz-line)] focus:border-[var(--pz-red)] outline-none text-white"
                >
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">Message <span className="text-[var(--pz-red)]">*</span></label>
              <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-5 py-3 rounded-md bg-[var(--pz-ink)] border border-[var(--pz-line)] focus:border-[var(--pz-red)] outline-none text-white" />
            </div>
            <button disabled={loading} data-testid="contact-submit" className="btn-red w-full justify-center disabled:opacity-60">{loading ? "Sending..." : <>Send Message <Send className="h-4 w-4" /></>}</button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">{label}{required && <span className="text-[var(--pz-red)]"> *</span>}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-5 py-3 rounded-md bg-[var(--pz-ink)] border border-[var(--pz-line)] focus:border-[var(--pz-red)] outline-none text-white" />
    </div>
  );
}
